import { useState } from "react";
import { api } from "../App";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";
import {
  Star,
  Loader2,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  Check,
  User as UserIcon,
  Car,
  MessageSquare,
  ShieldAlert,
} from "lucide-react";
import { toast } from "sonner";

const HOST_CRITERIA = [
  { key: "communication", label: "Comunicación" },
  { key: "punctuality", label: "Puntualidad (entrega/devolución)" },
  { key: "professionalism", label: "Profesionalismo" },
  { key: "accuracy", label: "Veracidad del listado" },
  { key: "overall", label: "Experiencia general" },
];

const VEHICLE_CRITERIA = [
  { key: "cleanliness", label: "Limpieza" },
  { key: "mechanical", label: "Estado mecánico" },
  { key: "comfort", label: "Comodidad" },
  { key: "accuracy", label: "Coincide con fotos/listado" },
  { key: "overall", label: "Calidad general" },
];

const HOST_FLAGS = [
  { key: "host_late_unresponsive", label: "Tardío o no responde" },
  { key: "host_unprofessional", label: "Comportamiento poco profesional" },
];
const VEHICLE_FLAGS = [
  { key: "vehicle_dirty", label: "Vehículo sucio" },
  { key: "vehicle_mechanical", label: "Problemas mecánicos" },
  { key: "vehicle_not_as_described", label: "No es como se describe" },
  { key: "safety_concerns", label: "Problemas de seguridad" },
];

function StarRow({ label, value, onChange, testid }) {
  return (
    <div className="flex items-center justify-between gap-3 py-2">
      <span className="text-sm text-neutral-700 flex-1">{label}</span>
      <div className="flex gap-1" data-testid={testid}>
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => onChange(n)}
            className="p-1 rounded hover:bg-neutral-100 transition"
            data-testid={`${testid}-${n}`}
          >
            <Star
              className={`w-5 h-5 ${
                value >= n ? "fill-amber-400 text-amber-400" : "text-neutral-300"
              }`}
            />
          </button>
        ))}
      </div>
    </div>
  );
}

export default function ReviewModal({ open, onOpenChange, booking, onSubmitted }) {
  const [step, setStep] = useState(0);
  const [hostScores, setHostScores] = useState({});
  const [vehicleScores, setVehicleScores] = useState({});
  const [comment, setComment] = useState("");
  const [flags, setFlags] = useState([]);
  const [critical, setCritical] = useState(false);
  const [criticalDetail, setCriticalDetail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setStep(0);
    setHostScores({});
    setVehicleScores({});
    setComment("");
    setFlags([]);
    setCritical(false);
    setCriticalDetail("");
  };

  const close = (changed) => {
    onOpenChange?.(false);
    setTimeout(reset, 300);
    if (changed) onSubmitted?.();
  };

  const toggleFlag = (key) =>
    setFlags((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));

  const canNextHost = !!hostScores.overall;
  const canNextVehicle = !!vehicleScores.overall;

  const submit = async () => {
    if (!hostScores.overall || !vehicleScores.overall) {
      toast.error("Completa la calificación general en ambas secciones");
      return;
    }
    if (critical && !criticalDetail.trim()) {
      toast.error("Describe brevemente el problema serio");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/reviews", {
        booking_id: booking.booking_id,
        host_criteria: hostScores,
        vehicle_criteria: vehicleScores,
        comment: comment || "",
        issue_flags: flags,
        critical_issue: critical,
        critical_issue_detail: critical ? criticalDetail : null,
      });
      toast.success("¡Gracias por tu reseña!");
      close(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo enviar la reseña");
    } finally {
      setSubmitting(false);
    }
  };

  const steps = [
    {
      title: "Califica al anfitrión",
      icon: UserIcon,
      content: (
        <div data-testid="step-host">
          <p className="text-sm text-neutral-500 mb-3">
            Tu opinión ayuda a otros huéspedes a elegir un buen anfitrión.
          </p>
          {HOST_CRITERIA.map((c) => (
            <StarRow
              key={c.key}
              label={c.label}
              value={hostScores[c.key] || 0}
              onChange={(v) => setHostScores({ ...hostScores, [c.key]: v })}
              testid={`host-${c.key}`}
            />
          ))}
        </div>
      ),
    },
    {
      title: "Califica el vehículo",
      icon: Car,
      content: (
        <div data-testid="step-vehicle">
          <p className="text-sm text-neutral-500 mb-3">
            Califica el estado y la calidad del vehículo de manera independiente.
          </p>
          {VEHICLE_CRITERIA.map((c) => (
            <StarRow
              key={c.key}
              label={c.label}
              value={vehicleScores[c.key] || 0}
              onChange={(v) => setVehicleScores({ ...vehicleScores, [c.key]: v })}
              testid={`vehicle-${c.key}`}
            />
          ))}
        </div>
      ),
    },
    {
      title: "Comentario (opcional)",
      icon: MessageSquare,
      content: (
        <div data-testid="step-comment">
          <p className="text-sm text-neutral-500 mb-3">
            Comparte detalles que ayuden a otros huéspedes.
          </p>
          <Textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Cuéntanos sobre tu viaje…"
            rows={5}
            data-testid="review-comment-input"
          />
        </div>
      ),
    },
    {
      title: "Reportar problemas",
      icon: ShieldAlert,
      content: (
        <div className="space-y-5" data-testid="step-flags">
          <div>
            <h4 className="font-semibold text-sm mb-2">Problemas con el anfitrión</h4>
            <div className="space-y-2">
              {HOST_FLAGS.map((f) => (
                <label key={f.key} className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox
                    checked={flags.includes(f.key)}
                    onCheckedChange={() => toggleFlag(f.key)}
                    data-testid={`flag-${f.key}`}
                  />
                  {f.label}
                </label>
              ))}
            </div>
          </div>
          <div>
            <h4 className="font-semibold text-sm mb-2">Problemas con el vehículo</h4>
            <div className="space-y-2">
              {VEHICLE_FLAGS.map((f) => (
                <label key={f.key} className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox
                    checked={flags.includes(f.key)}
                    onCheckedChange={() => toggleFlag(f.key)}
                    data-testid={`flag-${f.key}`}
                  />
                  {f.label}
                </label>
              ))}
            </div>
          </div>

          <div className="border-t pt-4">
            <label className="flex items-start gap-2 cursor-pointer">
              <Checkbox
                checked={critical}
                onCheckedChange={(v) => setCritical(!!v)}
                data-testid="flag-critical"
              />
              <div>
                <p className="text-sm font-semibold flex items-center gap-1 text-red-600">
                  <AlertTriangle className="w-4 h-4" />
                  Reportar problema serio
                </p>
                <p className="text-xs text-neutral-500">
                  Notifica al equipo de RivoNes para revisión inmediata.
                </p>
              </div>
            </label>
            {critical && (
              <Textarea
                value={criticalDetail}
                onChange={(e) => setCriticalDetail(e.target.value)}
                placeholder="Describe brevemente lo ocurrido…"
                rows={3}
                className="mt-3"
                data-testid="critical-detail-input"
              />
            )}
          </div>
        </div>
      ),
    },
  ];

  const cur = steps[step];
  const StepIcon = cur.icon;
  const isLast = step === steps.length - 1;
  const isFirst = step === 0;

  const canProceed =
    (step === 0 && canNextHost) ||
    (step === 1 && canNextVehicle) ||
    step === 2 ||
    step === 3;

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? onOpenChange?.(true) : close(false))}>
      <DialogContent className="max-w-lg" data-testid="review-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <StepIcon className="w-5 h-5 text-[#111111]" />
            {cur.title}
          </DialogTitle>
        </DialogHeader>

        {/* Progress dots */}
        <div className="flex gap-1.5 mt-2">
          {steps.map((_, i) => (
            <div
              key={i}
              className={`h-1 flex-1 rounded-full transition ${
                i <= step ? "bg-[#111111]" : "bg-neutral-200"
              }`}
            />
          ))}
        </div>

        <div className="mt-4 max-h-[60vh] overflow-y-auto pr-1">{cur.content}</div>

        <DialogFooter className="mt-4 flex flex-row justify-between gap-2 sm:justify-between">
          <Button
            variant="outline"
            onClick={() => (isFirst ? close(false) : setStep(step - 1))}
            disabled={submitting}
            className="flex-1 sm:flex-none"
            data-testid="review-back-btn"
          >
            {isFirst ? "Cancelar" : (
              <>
                <ChevronLeft className="w-4 h-4 mr-1" />
                Atrás
              </>
            )}
          </Button>
          {isLast ? (
            <Button
              onClick={submit}
              disabled={submitting || !canProceed}
              className="flex-1 sm:flex-none bg-[#111111] hover:bg-neutral-800 text-white"
              data-testid="review-submit-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
              Enviar reseña
            </Button>
          ) : (
            <Button
              onClick={() => setStep(step + 1)}
              disabled={!canProceed}
              className="flex-1 sm:flex-none bg-[#111111] hover:bg-neutral-800 text-white"
              data-testid="review-next-btn"
            >
              Siguiente
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
